/**
 * Redesigned Campaign Form - Professional UI/UX
 *
 * Modern, compact design with all options visible.
 * Features: Grid layout, visual hierarchy, inline filters.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { campaignSchema } from '../../lib/validationSchemas';
import type { Campaign, CreateCampaignData } from '../../types/campaign';
import type { User } from '../../types/auth';
import { useAuthStore } from '../../store/authStore';
import { TagSelector } from './TagSelector';
import { ABTestBuilder } from './ABTestBuilder';

interface CampaignFormProps {
  campaign?: Campaign;
  managers?: User[];
  onSubmit: (data: CreateCampaignData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  maxDomains?: number;
}

// Available options for filters - Comprehensive list of all countries
const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan', flag: '🇦🇫' },
  { code: 'AL', name: 'Albania', flag: '🇦🇱' },
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿' },
  { code: 'AD', name: 'Andorra', flag: '🇦🇩' },
  { code: 'AO', name: 'Angola', flag: '🇦🇴' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'AM', name: 'Armenia', flag: '🇦🇲' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'BY', name: 'Belarus', flag: '🇧🇾' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'BZ', name: 'Belize', flag: '🇧🇿' },
  { code: 'BJ', name: 'Benin', flag: '🇧🇯' },
  { code: 'BT', name: 'Bhutan', flag: '🇧🇹' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴' },
  { code: 'BA', name: 'Bosnia', flag: '🇧🇦' },
  { code: 'BW', name: 'Botswana', flag: '🇧🇼' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'BN', name: 'Brunei', flag: '🇧🇳' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫' },
  { code: 'BI', name: 'Burundi', flag: '🇧🇮' },
  { code: 'KH', name: 'Cambodia', flag: '🇰🇭' },
  { code: 'CM', name: 'Cameroon', flag: '🇨🇲' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'CV', name: 'Cape Verde', flag: '🇨🇻' },
  { code: 'CF', name: 'Central African Republic', flag: '🇨🇫' },
  { code: 'TD', name: 'Chad', flag: '🇹🇩' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'KM', name: 'Comoros', flag: '🇰🇲' },
  { code: 'CG', name: 'Congo', flag: '🇨🇬' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺' },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'DJ', name: 'Djibouti', flag: '🇩🇯' },
  { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻' },
  { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶' },
  { code: 'ER', name: 'Eritrea', flag: '🇪🇷' },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹' },
  { code: 'FJ', name: 'Fiji', flag: '🇫🇯' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'GA', name: 'Gabon', flag: '🇬🇦' },
  { code: 'GM', name: 'Gambia', flag: '🇬🇲' },
  { code: 'GE', name: 'Georgia', flag: '🇬🇪' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹' },
  { code: 'GN', name: 'Guinea', flag: '🇬🇳' },
  { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼' },
  { code: 'GY', name: 'Guyana', flag: '🇬🇾' },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
  { code: 'IS', name: 'Iceland', flag: '🇮🇸' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'CI', name: 'Ivory Coast', flag: '🇨🇮' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴' },
  { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
  { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬' },
  { code: 'LA', name: 'Laos', flag: '🇱🇦' },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧' },
  { code: 'LS', name: 'Lesotho', flag: '🇱🇸' },
  { code: 'LR', name: 'Liberia', flag: '🇱🇷' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾' },
  { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮' },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
  { code: 'MO', name: 'Macau', flag: '🇲🇴' },
  { code: 'MK', name: 'Macedonia', flag: '🇲🇰' },
  { code: 'MG', name: 'Madagascar', flag: '🇲🇬' },
  { code: 'MW', name: 'Malawi', flag: '🇲🇼' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'MV', name: 'Maldives', flag: '🇲🇻' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹' },
  { code: 'MR', name: 'Mauritania', flag: '🇲🇷' },
  { code: 'MU', name: 'Mauritius', flag: '🇲🇺' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'MD', name: 'Moldova', flag: '🇲🇩' },
  { code: 'MC', name: 'Monaco', flag: '🇲🇨' },
  { code: 'MN', name: 'Mongolia', flag: '🇲🇳' },
  { code: 'ME', name: 'Montenegro', flag: '🇲🇪' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦' },
  { code: 'MZ', name: 'Mozambique', flag: '🇲🇿' },
  { code: 'MM', name: 'Myanmar', flag: '🇲🇲' },
  { code: 'NA', name: 'Namibia', flag: '🇳🇦' },
  { code: 'NP', name: 'Nepal', flag: '🇳🇵' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'KP', name: 'North Korea', flag: '🇰🇵' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸' },
  { code: 'PA', name: 'Panama', flag: '🇵🇦' },
  { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'SN', name: 'Senegal', flag: '🇸🇳' },
  { code: 'RS', name: 'Serbia', flag: '🇷🇸' },
  { code: 'SC', name: 'Seychelles', flag: '🇸🇨' },
  { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰' },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮' },
  { code: 'SO', name: 'Somalia', flag: '🇸🇴' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'SS', name: 'South Sudan', flag: '🇸🇸' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩' },
  { code: 'SR', name: 'Suriname', flag: '🇸🇷' },
  { code: 'SZ', name: 'Swaziland', flag: '🇸🇿' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'SY', name: 'Syria', flag: '🇸🇾' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼' },
  { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬' },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿' },
  { code: 'VU', name: 'Vanuatu', flag: '🇻🇺' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪' },
  { code: 'ZM', name: 'Zambia', flag: '🇿🇲' },
  { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼' },
];

const LANGUAGES = [
  { code: 'af', name: 'Afrikaans' },
  { code: 'sq', name: 'Albanian' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'eu', name: 'Basque' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bn', name: 'Bengali' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'en', name: 'English' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr', name: 'French' },
  { code: 'gl', name: 'Galician' },
  { code: 'ka', name: 'Georgian' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ga', name: 'Irish' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'kn', name: 'Kannada' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'km', name: 'Khmer' },
  { code: 'ko', name: 'Korean' },
  { code: 'ku', name: 'Kurdish' },
  { code: 'ky', name: 'Kyrgyz' },
  { code: 'lo', name: 'Lao' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'ms', name: 'Malay' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mt', name: 'Maltese' },
  { code: 'mr', name: 'Marathi' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'ne', name: 'Nepali' },
  { code: 'no', name: 'Norwegian' },
  { code: 'ps', name: 'Pashto' },
  { code: 'fa', name: 'Persian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'si', name: 'Sinhala' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'so', name: 'Somali' },
  { code: 'es', name: 'Spanish' },
  { code: 'sw', name: 'Swahili' },
  { code: 'sv', name: 'Swedish' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'tg', name: 'Tajik' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'cy', name: 'Welsh' },
  { code: 'yi', name: 'Yiddish' },
];

const OPERATING_SYSTEMS = [
  { code: 'Windows', name: 'Windows', icon: '🪟' },
  { code: 'macOS', name: 'macOS', icon: '🍎' },
  { code: 'Linux', name: 'Linux', icon: '🐧' },
  { code: 'iOS', name: 'iOS', icon: '📱' },
  { code: 'Android', name: 'Android', icon: '🤖' },
  { code: 'Chrome OS', name: 'Chrome OS', icon: '🌐' },
];

// ─── SearchableMultiSelect ──────────────────────────────────────────────────

interface MultiSelectOption {
  code: string;
  name: string;
  flag?: string;
  icon?: string;
}

interface SearchableMultiSelectProps {
  label: string;
  variant: 'allow' | 'block';
  options: MultiSelectOption[];
  value: string[] | null | undefined;
  onChange: (value: string[] | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  label,
  variant,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  const { t } = useTranslation('campaigns');
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selected = value ?? [];

  // Close on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q));
  }, [search, options]);

  const toggle = (code: string) => {
    const next = selected.includes(code)
      ? selected.filter(c => c !== code)
      : [...selected, code];
    onChange(next.length ? next : null);
    setSearch('');
  };

  const remove = (code: string) => {
    const next = selected.filter(c => c !== code);
    onChange(next.length ? next : null);
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const isAllow = variant === 'allow';
  const accentRing   = isAllow ? 'focus-within:ring-emerald-400/40 focus-within:border-emerald-400' : 'focus-within:ring-red-400/40 focus-within:border-red-400';
  const chipBg       = isAllow ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200';
  const chipRemove   = isAllow ? 'hover:text-emerald-600' : 'hover:text-red-600';
  const checkColor   = isAllow ? 'text-emerald-500' : 'text-red-500';
  const labelColor   = isAllow ? 'text-emerald-700' : 'text-red-700';
  const labelBg      = isAllow ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      {/* Label */}
      <span className={`inline-flex items-center gap-1.5 self-start text-xs font-semibold px-2 py-0.5 rounded-md border ${labelBg} ${labelColor}`}>
        {isAllow ? t('filters.allow_label') : t('filters.block_label')}
      </span>

      {/* Trigger / input area */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleOpen}
        onKeyDown={e => e.key === 'Enter' && handleOpen()}
        className={`relative min-h-[38px] w-full flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 bg-white border rounded-lg cursor-text transition-all ring-2 ring-transparent ${accentRing} ${
          open ? (isAllow ? 'border-emerald-400' : 'border-red-400') : 'border-gray-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
      >
        {/* Selected chips */}
        {selected.map(code => {
          const opt = options.find(o => o.code === code);
          return (
            <span key={code} className={`inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded border text-xs font-medium ${chipBg}`}>
              {(opt?.flag || opt?.icon) && (
                <span className="text-sm leading-none">{opt.flag || opt.icon}</span>
              )}
              <span>{opt?.name ?? code}</span>
              <button
                type="button"
                onMouseDown={e => { e.stopPropagation(); remove(code); }}
                disabled={disabled}
                className={`ml-0.5 opacity-50 ${chipRemove} transition-colors leading-none`}
                aria-label={`Remove ${opt?.name ?? code}`}
              >×</button>
            </span>
          );
        })}

        {/* Search input (visible when open or no selection) */}
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && (setOpen(false), setSearch(''))}
            className="flex-1 min-w-[100px] text-sm outline-none bg-transparent"
            placeholder={t('filters.search_placeholder')}
          />
        ) : (
          selected.length === 0 && (
            <span className="text-sm text-gray-400 select-none">{placeholder ?? t('filters.select_placeholder')}</span>
          )
        )}

        {/* Caret */}
        <span className={`ml-auto text-gray-400 text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="relative z-20">
          <div className="absolute top-0 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-400 text-center">{t('filters.no_results', { search })}</p>
            ) : (
              <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
                {filtered.map(opt => {
                  const isSel = selected.includes(opt.code);
                  return (
                    <li key={opt.code} role="option" aria-selected={isSel}>
                      <button
                        type="button"
                        onMouseDown={e => { e.preventDefault(); toggle(opt.code); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                          isSel ? (isAllow ? 'bg-emerald-50' : 'bg-red-50') : 'hover:bg-gray-50'
                        }`}
                      >
                        {(opt.flag || opt.icon) && (
                          <span className="text-base leading-none w-5 text-center">{opt.flag || opt.icon}</span>
                        )}
                        <span className="flex-1">{opt.name}</span>
                        {isSel && <span className={`text-sm font-bold ${checkColor}`}>✓</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CampaignFormRedesigned: React.FC<CampaignFormProps> = ({
  campaign,
  managers = [],
  onSubmit,
  onCancel,
  isLoading = false,
  maxDomains = 10,
}) => {
  const { t } = useTranslation('campaigns');
  const methods = useForm({
    resolver: zodResolver(campaignSchema),
    mode: 'onBlur',
    defaultValues: campaign ? {
      name: campaign.name,
      human_redirect_url: campaign.human_redirect_url,
      human_redirect_type: campaign.human_redirect_type || 'url',
      human_redirect_mode: campaign.human_redirect_mode || 'redirect',
      human_back_redirect_url: campaign.human_back_redirect_url || '',
      bot_redirect_url: campaign.bot_redirect_url,
      bot_redirect_type: campaign.bot_redirect_type || 'url',
      domains: campaign.domains && campaign.domains.length > 0 ? campaign.domains.map((d: any) => typeof d === 'string' ? d : d.domain) : [''],
      status: campaign.status,
      operation_mode: campaign.operation_mode || 'filter',
      assigned_manager_id: campaign.assigned_manager_id,
      tags: campaign.tags || [],
      filter: campaign.filter || {
        allowed_countries: null,
        blocked_countries: null,
        allowed_languages: null,
        blocked_languages: null,
        allowed_os: null,
        blocked_os: null,
      },
      target_mode: campaign.target_mode || 'default',
      split_links: campaign.split_links || [],
    } : {
      human_redirect_type: 'url',
      human_redirect_mode: 'redirect',
      human_back_redirect_url: '',
      bot_redirect_type: 'url',
      status: 'active',
      operation_mode: 'filter',
      domains: [''],
      tags: [],
      filter: {
        allowed_countries: null,
        blocked_countries: null,
        allowed_languages: null,
        blocked_languages: null,
        allowed_os: null,
        blocked_os: null,
      },
      target_mode: 'default',
      split_links: [],
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = methods;

  const authUser = useAuthStore((s) => s.user);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'domains',
  });

  /**
   * Normalize domain input by:
   * 1. Removing protocol (http://, https://)
   * 2. Removing www. prefix
   * 3. Extracting base domain from subdomains (e.g., test.example.com -> example.com)
   * 4. Removing trailing slashes and paths
   */
  const normalizeDomain = (input: string): string => {
    if (!input) return '';

    let domain = input.trim().toLowerCase();

    // Remove protocol
    domain = domain.replace(/^https?:\/\//i, '');

    // Remove www. prefix
    domain = domain.replace(/^www\./i, '');

    // Remove trailing slashes and paths
    domain = domain.split('/')[0];

    // Remove port if present
    domain = domain.split(':')[0];

    // Strip subdomains while preserving 2-part TLDs (ccTLDs like .com.ua, .co.uk, .net.au).
    // A 2-letter TLD is a country code — the registrable domain is 3 parts (sld.cc-sld.cc),
    // so e.g. evrozabor-pro.com.ua must NOT be reduced to com.ua.
    const parts = domain.split('.');
    if (parts.length > 2) {
      const tld = parts[parts.length - 1];
      const keepParts = tld.length === 2 ? 3 : 2;
      if (parts.length > keepParts) {
        domain = parts.slice(-keepParts).join('.');
      }
    }

    return domain;
  };

  const handleDomainBlur = (index: number, value: string) => {
    const normalized = normalizeDomain(value);
    if (normalized !== value) {
      setValue(`domains.${index}` as const, normalized, { shouldValidate: true });
    }
  };

  const handleFormSubmit = async (data: any) => {
    const submitData = {
      ...data,
      tag_ids: data.tags?.map((tag: any) => tag.id),
    };
    delete submitData.tags;
    await onSubmit(submitData as CreateCampaignData);
  };

  return (
    <FormProvider {...methods}>
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Essential Settings - Compact Grid */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-lg">⚙️</span>
          {t('form.settings_title')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Campaign Name */}
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('form.name_label')} <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              {...register('name')}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('form.name_placeholder_example')}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name.message as string}</p>
            )}
          </div>

          {/* Human Page — hidden in Monitor Only mode (no redirect happens there) */}
          <div className={watch('operation_mode') === 'monitor' ? 'hidden' : ''}>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('form.human_url_label')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="url"
                  {...register('human_redirect_type')}
                  className="mr-2"
                />
                <span className="text-sm">{t('form.type_full_url')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="path"
                  {...register('human_redirect_type')}
                  className="mr-2"
                />
                <span className="text-sm">{t('form.type_relative_path')}</span>
              </label>
            </div>
            <input
              id="human_redirect_url"
              type="text"
              {...register('human_redirect_url')}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.human_redirect_url ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={watch('human_redirect_type') === 'path' ? 'landing/user.php' : 'https://yoursite.com/offer'}
            />
            {errors.human_redirect_url && (
              <p className="mt-1 text-xs text-red-600">{errors.human_redirect_url.message as string}</p>
            )}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('form.load_mode_label')}</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="redirect"
                    {...register('human_redirect_mode')}
                    className="mr-2"
                  />
                  <span className="text-sm">{t('form.load_mode_redirect')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="iframe"
                    {...register('human_redirect_mode')}
                    className="mr-2"
                  />
                  <span className="text-sm">{t('form.load_mode_iframe')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="require"
                    {...register('human_redirect_mode')}
                    className="mr-2"
                  />
                  <span className="text-sm">{t('form.load_mode_require')}</span>
                </label>
              </div>
            </div>
            {watch('human_redirect_mode') === 'iframe' && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('form.back_redirect_label')}
                </label>
                <input
                  id="human_back_redirect_url"
                  type="text"
                  {...register('human_back_redirect_url')}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.human_back_redirect_url ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="https://yoursite.com/page-2"
                />
                {errors.human_back_redirect_url && (
                  <p className="mt-1 text-xs text-red-600">{errors.human_back_redirect_url.message as string}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">{t('form.back_redirect_hint')}</p>
              </div>
            )}
          </div>

          {/* Bot Page — hidden in Monitor Only mode (no redirect happens there) */}
          <div className={watch('operation_mode') === 'monitor' ? 'hidden' : ''}>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('form.bot_url_label')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="url"
                  {...register('bot_redirect_type')}
                  className="mr-2"
                />
                <span className="text-sm">{t('form.type_full_url')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="path"
                  {...register('bot_redirect_type')}
                  className="mr-2"
                />
                <span className="text-sm">{t('form.type_relative_path')}</span>
              </label>
            </div>
            <input
              id="bot_redirect_url"
              type="text"
              {...register('bot_redirect_url')}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.bot_redirect_url ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={watch('bot_redirect_type') === 'path' ? 'landing/bot.html' : 'https://yoursite.com/safe'}
            />
            {errors.bot_redirect_url && (
              <p className="mt-1 text-xs text-red-600">{errors.bot_redirect_url.message as string}</p>
            )}
          </div>

          {/* Domains */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-gray-700">
                {t('form.domains_label')} <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                {t('form.domains_count', { count: fields.length, max: maxDomains })}
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {t('form.domains_hint')}
            </p>
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex flex-col gap-1">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      {...register(`domains.${index}` as const, {
                        onBlur: (e) => handleDomainBlur(index, e.target.value),
                      })}
                      className={`flex-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.domains?.[index] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="example.com"
                      disabled={isLoading}
                    />
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        disabled={isLoading}
                        className="px-3 py-2 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 disabled:opacity-50"
                      >
                        {t('form.domain_remove')}
                      </button>
                    )}
                  </div>
                  {errors.domains?.[index] && (
                    <p className="text-xs text-red-600">{(errors.domains[index] as any)?.message as string}</p>
                  )}
                </div>
              ))}
              {errors.domains && !Array.isArray(errors.domains) && (
                <p className="text-xs text-red-600">{errors.domains.message as string}</p>
              )}
              {fields.length >= maxDomains && (
                <p className="text-xs text-amber-600">
                  {t('form.domain_limit_reached')}
                </p>
              )}
              <button
                type="button"
                onClick={() => append('')}
                disabled={isLoading || fields.length >= maxDomains}
                className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-300 rounded-md hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('form.domain_add')}
              </button>
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('form.status_label')}
            </label>
            <select
              id="status"
              {...register('status')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="active">🟢 {t('form.status_active')}</option>
              <option value="paused">🟡 {t('form.status_paused')}</option>
              <option value="archived">⚫ {t('form.status_archived')}</option>
            </select>
          </div>

          {/* Operation mode + estimated CPC */}
          <div>
            <label htmlFor="operation_mode" className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('form.operation_mode_label')}
            </label>
            <select
              id="operation_mode"
              {...register('operation_mode')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="filter">{t('form.operation_mode_filter')}</option>
              <option value="monitor">{t('form.operation_mode_monitor')}</option>
            </select>
          </div>

          {watch('operation_mode') === 'monitor' && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4 text-sm">
              <p className="font-medium text-indigo-800">{t('form.monitor_title')}</p>
              <p className="text-indigo-700 mt-1">{t('form.monitor_desc')}</p>
              {campaign ? (
                <>
                  <p className="text-xs font-semibold text-indigo-800 mt-3">{t('form.monitor_php')}</p>
                  <pre className="mt-1 overflow-x-auto rounded bg-white border border-indigo-100 p-2 text-xs text-gray-800">{`<?php
$apiUrl='${window.location.protocol}//${window.location.hostname}:8081';
$campaignUuid='${campaign.uuid}';
$userId='${authUser?.id ?? 'YOUR_USER_ID'}';
$apiKey='${campaign.api_key}';
require __DIR__.'/axetra-monitor-beacon.php'; // fills these in`}</pre>
                  <p className="text-xs font-semibold text-indigo-800 mt-3">{t('form.monitor_js')}</p>
                  <pre className="mt-1 overflow-x-auto rounded bg-white border border-indigo-100 p-2 text-xs text-gray-800">{`<script src="/axetra-monitor-beacon.js"
  data-uuid="${campaign.uuid}"
  data-user="${authUser?.id ?? 'YOUR_USER_ID'}"
  data-key="${campaign.api_key}"></script>`}</pre>
                </>
              ) : (
                <p className="text-indigo-600 mt-2 text-xs">{t('form.monitor_save_first')}</p>
              )}
            </div>
          )}

          {/* Manager Assignment */}
          {managers.length > 0 && (
            <div>
              <label htmlFor="assigned_manager_id" className="block text-xs font-medium text-gray-700 mb-1.5">
                {t('form.manager_label')}
              </label>
              <select
                id="assigned_manager_id"
                {...register('assigned_manager_id', { valueAsNumber: true })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">{t('form.no_manager')}</option>
                {managers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Traffic Filters */}
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-lg">🎯</span>
          {t('filters.title')}
        </h3>
        <div className="space-y-5">

          {/* Country Filters */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🌍</span> {t('filters.geo_targeting')}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Controller name="filter.allowed_countries" control={control}
                render={({ field }) => (
                  <SearchableMultiSelect label={t('filters.allow_label')} variant="allow"
                    options={COUNTRIES} value={field.value} onChange={field.onChange}
                    placeholder={t('filters.all_countries_allowed')} disabled={isLoading} />
                )}
              />
              <Controller name="filter.blocked_countries" control={control}
                render={({ field }) => (
                  <SearchableMultiSelect label={t('filters.block_label')} variant="block"
                    options={COUNTRIES} value={field.value} onChange={field.onChange}
                    placeholder={t('filters.no_countries_blocked')} disabled={isLoading} />
                )}
              />
            </div>
          </div>

          {/* Language Filters */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🗣️</span> {t('filters.language_targeting')}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Controller name="filter.allowed_languages" control={control}
                render={({ field }) => (
                  <SearchableMultiSelect label={t('filters.allow_label')} variant="allow"
                    options={LANGUAGES} value={field.value} onChange={field.onChange}
                    placeholder={t('filters.all_languages_allowed')} disabled={isLoading} />
                )}
              />
              <Controller name="filter.blocked_languages" control={control}
                render={({ field }) => (
                  <SearchableMultiSelect label={t('filters.block_label')} variant="block"
                    options={LANGUAGES} value={field.value} onChange={field.onChange}
                    placeholder={t('filters.no_languages_blocked')} disabled={isLoading} />
                )}
              />
            </div>
          </div>

          {/* OS Filters */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>💻</span> {t('filters.os_targeting')}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Controller name="filter.allowed_os" control={control}
                render={({ field }) => (
                  <SearchableMultiSelect label={t('filters.allow_label')} variant="allow"
                    options={OPERATING_SYSTEMS} value={field.value} onChange={field.onChange}
                    placeholder={t('filters.all_os_allowed')} disabled={isLoading} />
                )}
              />
              <Controller name="filter.blocked_os" control={control}
                render={({ field }) => (
                  <SearchableMultiSelect label={t('filters.block_label')} variant="block"
                    options={OPERATING_SYSTEMS} value={field.value} onChange={field.onChange}
                    placeholder={t('filters.no_os_blocked')} disabled={isLoading} />
                )}
              />
            </div>
          </div>

        </div>
      </div>

      {/* A/B Testing */}
      <ABTestBuilder disabled={isLoading} />

      {/* Tags - Compact */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <span className="text-lg">🏷️</span>
          {t('form.tags_title')}
        </h3>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <TagSelector
              selectedTags={field.value || []}
              onChange={field.onChange}
              disabled={isLoading}
            />
          )}
        />
      </div>

      {/* Action Buttons - Sticky */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-6 px-6 py-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          disabled={isLoading}
        >
          {t('form.cancel')}
        </button>
        <button
          type="submit"
          className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>{t('form.saving')}</span>
            </>
          ) : (
            <>
              <span>{campaign ? `✓ ${t('form.submit_update')}` : `+ ${t('form.submit_create')}`}</span>
            </>
          )}
        </button>
      </div>
    </form>
    </FormProvider>
  );
};

CampaignFormRedesigned.displayName = 'CampaignFormRedesigned';
